import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { SodInsights } from '../../../../shared/components';
import { RENDER_MODES } from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import { type InsightItemTypes } from '../../../../shared/types';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useGetInsights } from '../../hooks';

import ViolationGraphCluster from './ViolationsGraphCluster';
import { ViolationsInsightsFooter } from './ViolationsGraphClusterShared';

import './ViolationsInsights.css';

interface ViolationsInsightsProps {
  className?: string;
}

/**
 * ViolationsInsights
 */
function ViolationsInsights({ className }: ViolationsInsightsProps) {
  const dispatch = useDispatch();

  const {
    aag: {
      global: { isGlobalLoading },
      violationsTab: {
        controls: {
          isInsightsOpen,
          activeFilters,
          tabInAction,
          renderMode,
          searchKey,
          sortInAction,
          activeInsight,
          activeGraphPoint,
        },
      },
    },
  } = useTypedSelector((state) => state);

  const [insightsData, setInsightsData] = useState<InsightItemTypes[] | null>(
    null
  );

  const isGraphMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.GRAPH),
    [renderMode]
  );

  const { handleFetchInsights, isError, isLoading } = useGetInsights();

  const handleInsightsFetch = useCallback(() => {
    // BELOW DISPATCHES AND STATES ARE RESPONSIBLE FOR RESET OF `GRAPH` / `INSIGHT` / `WIDGET`, WHEN CHANGES ARE MADE TO `TABS`, `FILTERS`, `SEARCH` AND `SORT`
    // IN CASE OF `GRAPH` AND `WIDGET`, `SORT` ACTION IS DISABLED
    setInsightsData(null);
    batch(() => {
      dispatch(violationsTabActions.setActiveGraphPoint(null));
      dispatch(violationsTabActions.setGraphRowContext(null));
      dispatch(violationsTabActions.setActiveWidgetItem(null));
    });

    // API call function
    handleFetchInsights(activeFilters, tabInAction, searchKey, sortInAction)
      .then((response) => {
        const typedResponse = response as InsightItemTypes[];

        setInsightsData(typedResponse);
      })
      .catch((error) => {
        Logger.log('error', error);
      });
  }, [
    dispatch,
    activeFilters,
    tabInAction,
    searchKey,
    sortInAction,
    handleFetchInsights,
  ]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        handleInsightsFetch();
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [isGlobalLoading, handleInsightsFetch]);

  // Insights inbuilt actions
  const handleIsInsights = useCallback(() => {
    dispatch(violationsTabActions.setIsInsightsOpen(!isInsightsOpen));
  }, [dispatch, isInsightsOpen]);

  const handleOptionClick = useCallback(
    (insightItem: InsightItemTypes | null) => {
      dispatch(violationsTabActions.setActiveInsight(insightItem));
    },
    [dispatch]
  );

  const isEntitlementContext = window.location.pathname.includes('entitlement');

  if (activeGraphPoint) {
    return <ViolationGraphCluster />;
  }

  // For entitlement tab, don't show SodInsights
  if (isEntitlementContext) {
    return null;
  }

  return (
    isInsightsOpen && (
      <SodInsights
        data={insightsData}
        isLoading={Boolean(isLoading || (!insightsData && !isError))}
        isError={isError}
        activeInsight={activeInsight}
        isInsightsOpen={isInsightsOpen}
        setIsInsightsOpen={handleIsInsights}
        handleRefresh={handleInsightsFetch}
        handleOptionClick={handleOptionClick}
        footerContent={
          <ViolationsInsightsFooter
            isGraphMode={isGraphMode}
            onClick={() => {
              if (isGraphMode) {
                dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));

                return;
              }

              dispatch(violationsTabActions.setRenderMode(RENDER_MODES.GRAPH));
            }}
          />
        }
        className={className}
      />
    )
  );
}

ViolationsInsights.defaultProps = {
  className: null,
};

export default ViolationsInsights;
